import { useTranslation } from "react-i18next";

import { Button } from "@digital-services/ui";

import { OrganizationStatus } from "@organization/models/OrganizationDetails";
import { useGetOrganizationDetails } from "@organization/services/use-organization";
import { InviteUserForm } from "@users/organizationuser/components/InviteUserForm";

import { useDrawerWithConfirm } from "@hooks/useDrawerWithConfirm";
import { useUserContext } from "@hooks/useUserContext";
import InviteUserIcon from "@icons/invite_user.svg?react";

import { InviteFormContextProvider } from "../context";

interface Props {
  beforeDrawerOpen?: () => void;
}

export function InviteOrganizationUserButton(props: Readonly<Props>) {
  const { organization, isLoading } = useGetOrganizationDetails();
  const { beforeDrawerOpen } = props;

  const { t } = useTranslation(undefined, { keyPrefix: "organization.users" });

  const { user } = useUserContext();
  const { showDrawer, hideDrawer } = useDrawerWithConfirm();

  const inviteUser = (): void => {
    if (beforeDrawerOpen) {
      beforeDrawerOpen();
    }

    showDrawer({
      title: t("invite.title"),
      children: (
        <InviteFormContextProvider>
          <InviteUserForm
            onCancel={hideDrawer}
            onSuccess={() => {
              hideDrawer();
            }}
          />
        </InviteFormContextProvider>
      )
    });
  };

  if (!user.isAdministrator) return null;

  return (
    <Button
      isLoading={isLoading}
      disabled={organization?.status === OrganizationStatus.Inactive}
      className="min-w-max whitespace-nowrap"
      data-testid="invite-user-button"
      icon={<InviteUserIcon />}
      onClick={inviteUser}
      variant="primary"
    >
      {t("invite.label")}
    </Button>
  );
}
